import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import type { CheckStatus, StatusCheck, SystemStatus } from '../api/types'
import { PageHeader } from '../components/Layout'
import { ErrorBox, Loading } from '../components/States'
import { formatDateTime, formatTime } from '../lib/format'

const RANK: Record<CheckStatus, number> = { ok: 0, unknown: 1, warning: 2, down: 3 }
const SLOW_MS = 3000
const REFRESH_MS = 30_000

/**
 * Is each part of the stack working, and if not, what will staff notice?
 * The server judges every check except its own: when it doesn't answer, only
 * this page can say so.
 */
export function SystemStatusPage() {
  const query = useQuery({
    queryKey: ['system-status'],
    queryFn: async () => {
      const started = performance.now()
      const body = await api.get<SystemStatus>('/system/status')
      return { body, ms: Math.round(performance.now() - started) }
    },
    refetchInterval: REFRESH_MS,
    retry: false,
  })

  // A 403 (or any 4xx) means the server answered: show it as an error, not an outage.
  const serverAnswered = query.error instanceof ApiError && query.error.status < 500
  const server = serverRow(query.error, query.data)
  const rows = query.error || !query.data ? (server ? [server] : []) : [server!, ...query.data.body.checks]
  const checkedAt = query.error ? new Date(query.errorUpdatedAt) : query.data && new Date(query.data.body.checked_at)

  return (
    <>
      <PageHeader
        title="System status"
        subtitle={
          checkedAt
            ? `Checked ${formatTime(checkedAt.toISOString())} · refreshes every 30 s`
            : 'Checking…'
        }
      >
        <button className="btn ghost sm" onClick={() => query.refetch()} disabled={query.isFetching}>
          {query.isFetching ? 'Checking…' : 'Refresh'}
        </button>
      </PageHeader>

      <div className="content stack">
        {query.isPending ? (
          <Loading label="Checking…" />
        ) : serverAnswered ? (
          <ErrorBox error={query.error} />
        ) : (
          <>
            <Banner rows={rows} />
            <div className="card status-list">
              {rows.map((row) => (
                <CheckRow key={row.key} check={row} />
              ))}
            </div>
            {query.error ? (
              <p className="status-note">The other checks run on the server, so they can't be shown until it answers.</p>
            ) : null}
          </>
        )}
      </div>
    </>
  )
}

/** The "Fulfil server" row, from how (and whether) the request came back. */
function serverRow(error: unknown, data: { body: SystemStatus; ms: number } | undefined): StatusCheck | null {
  const name = 'Fulfil server'
  if (error instanceof ApiError) {
    return {
      key: 'server',
      name,
      status: 'down',
      summary: `Not responding (HTTP ${error.status})`,
      affects: 'Nothing in Fulfil works until it is back — the app and this dashboard both need it.',
      details: [{ label: 'Problem', value: error.detail }],
    }
  }
  if (error) {
    return {
      key: 'server',
      name,
      status: 'down',
      summary: 'Not reachable',
      affects:
        "Check this computer's internet connection. If other sites load, the Fulfil server is down and nothing in Fulfil works until it is back.",
      details: [],
    }
  }
  if (!data) return null
  const { body, ms } = data
  const details = [
    { label: 'Response time', value: `${ms.toLocaleString()} ms` },
    { label: 'Version', value: body.server.version ?? 'unknown' },
    { label: 'Running since', value: formatDateTime(body.server.started_at) },
    { label: 'Environment', value: body.server.environment },
  ]
  const version = body.server.version ? ` · ${body.server.version}` : ''
  if (ms > SLOW_MS) {
    return {
      key: 'server',
      name,
      status: 'warning',
      summary: `Slow · ${(ms / 1000).toFixed(1)} s`,
      affects: 'Screens take longer to load.',
      details,
    }
  }
  return { key: 'server', name, status: 'ok', summary: `Up · ${ms} ms${version}`, affects: null, details }
}

function Banner({ rows }: { rows: StatusCheck[] }) {
  const problems = rows.filter((row) => row.status !== 'ok').sort((a, b) => RANK[b.status] - RANK[a.status])
  const worst = problems[0]
  if (!worst) {
    return (
      <div className="status-banner ok" role="status">
        <span className="status-dot ok" />
        <div>
          <strong>All systems working</strong>
        </div>
      </div>
    )
  }
  if (worst.status === 'unknown') {
    return (
      <div className="status-banner unknown" role="status">
        <span className="status-dot unknown" />
        <div>
          <strong>Not everything could be checked</strong>
          <div>
            {problems.length === 1 ? "1 check couldn't run" : `${problems.length} checks couldn't run`}; nothing
            is known to be broken.
          </div>
        </div>
      </div>
    )
  }
  const more = problems.filter((row) => row.status !== 'unknown').length - 1
  return (
    <div className={`status-banner ${worst.status}`} role="status">
      <span className={`status-dot ${worst.status}`} />
      <div>
        <strong>{`${worst.name}: ${worst.summary}`}</strong>
        {worst.affects ? <div>{worst.affects}</div> : null}
        {more > 0 ? <div className="status-more">{more === 1 ? '1 more problem' : `${more} more problems`} below.</div> : null}
      </div>
    </div>
  )
}

const LABEL: Record<CheckStatus, string> = { ok: 'Working', warning: 'Problem', down: 'Down', unknown: 'Unknown' }

function CheckRow({ check }: { check: StatusCheck }) {
  return (
    <details open={check.status === 'down' || check.status === 'warning'}>
      <summary>
        <span className={`status-dot ${check.status}`} title={LABEL[check.status]} />
        <span className="status-name">{check.name}</span>
        <span className="status-summary">
          <span className="sr-only">{LABEL[check.status]}: </span>
          {check.summary}
        </span>
      </summary>
      <div className="status-body">
        {check.affects ? <div className={`status-affects ${check.status}`}>{check.affects}</div> : null}
        {check.details.length ? (
          <dl>
            {check.details.map((detail) => (
              <div key={detail.label}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </details>
  )
}
